/**
 * Activity Timeline Component
 *
 * Displays a timeline of activities for an opportunity
 */

import { useState } from 'react';
import { Plus, Filter, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOpportunityActivities } from '../../hooks';
import { ACTIVITY_TYPES } from '../../constants';
import { ActivityCard } from './ActivityCard';
import type { CrmActivityDetail } from '../../types';

interface ActivityTimelineProps {
  accountId: string;
  opportunityId: string;
  onAddActivity?: () => void;
  onEditActivity?: (activity: CrmActivityDetail) => void;
  onCompleteActivity?: (activity: CrmActivityDetail) => void;
  onDeleteActivity?: (activity: CrmActivityDetail) => void;
}

export function ActivityTimeline({
  accountId,
  opportunityId,
  onAddActivity,
  onEditActivity,
  onCompleteActivity,
  onDeleteActivity,
}: ActivityTimelineProps) {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const { data, isLoading, error } = useOpportunityActivities(accountId, opportunityId, {
    status: statusFilter || undefined,
    type: typeFilter || undefined,
  });

  if (isLoading) {
    return <ActivityTimelineSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-red-500">Failed to load activities</p>
        <p className="text-xs text-gray-400 mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  const activities = data?.activities || [];
  const summary = data?.summary;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Activities</h3>
          {summary && (
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {summary.scheduled} scheduled
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {summary.completed} completed
              </span>
            </div>
          )}
        </div>
        {onAddActivity && (
          <Button size="sm" onClick={onAddActivity}>
            <Plus className="h-4 w-4 mr-1" />
            Log Activity
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-400" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 h-8">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Status</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 h-8">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Types</SelectItem>
            {ACTIVITY_TYPES.map((type) => (
              <SelectItem key={type.key} value={type.key}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Timeline */}
      {activities.length === 0 ? (
        <EmptyState onAddActivity={onAddActivity} />
      ) : (
        <div className="mt-4">
          {activities.map((activity) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              variant="timeline"
              onComplete={onCompleteActivity}
              onEdit={onEditActivity}
              onDelete={onDeleteActivity}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onAddActivity }: { onAddActivity?: () => void }) {
  return (
    <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
      <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
      <p className="text-sm text-gray-500 mb-1">No activities recorded yet</p>
      <p className="text-xs text-gray-400 mb-4">
        Log calls, meetings, and emails to track your progress
      </p>
      {onAddActivity && (
        <Button variant="outline" size="sm" onClick={onAddActivity}>
          <Plus className="h-4 w-4 mr-1" />
          Log First Activity
        </Button>
      )}
    </div>
  );
}

function ActivityTimelineSkeleton() {
  return (
    <div className="space-y-4">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-4 w-40 mt-1" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>

      {/* Filter skeleton */}
      <div className="flex gap-2">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-8 w-40" />
      </div>

      {/* Timeline skeleton */}
      <div className="space-y-4 mt-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="relative pl-8 pb-6">
            <Skeleton className="absolute left-0 top-0 h-8 w-8 rounded-full" />
            <div className="bg-white border border-gray-200 rounded-lg p-4 ml-4">
              <Skeleton className="h-5 w-48 mb-2" />
              <Skeleton className="h-4 w-32 mb-3" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
